import type { ReactNode } from 'react';
import { ApiError } from '../lib/api.ts';
import { T } from '../lib/strings.ts';
import { cn } from '../lib/utils.ts';
import { Button } from './ui/button.tsx';
import { IconAlert, IconLock, IconRefresh, IconSearch } from './brand/icons.tsx';

/** שגיאה למשתמש: רק textHe מה-API (11 §4); kind נשמר כ-data לבדיקות. 403 — מנעול, 404 — חיפוש, השאר — אזהרה. */
export function ErrorView({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const e = error instanceof ApiError ? error : null;
  const Icon = e?.status === 403 ? IconLock : e?.status === 404 ? IconSearch : IconAlert;
  return (
    <div
      role="alert"
      data-testid="api-error"
      data-kind={e?.kind ?? 'unknown'}
      data-status={e?.status ?? 0}
      className="flex items-start gap-3 rounded-md border border-error/35 bg-error/[0.06] p-4"
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-circle bg-error/12 text-error">
        <Icon size={18} />
      </span>
      <div className="flex min-w-0 flex-col gap-2 pt-1.5">
        <p className="m-0 font-semibold">{e?.textHe ?? T.network}</p>
        {onRetry && e?.status !== 403 && e?.status !== 404 && (
          <Button variant="outline" size="sm" className="w-fit" onClick={onRetry}>
            <IconRefresh size={16} />
            {T.retry}
          </Button>
        )}
      </div>
    </div>
  );
}

/** טעינה: שלד (skeleton) עם טקסט נגיש; variant מתאים את הצורה למסך. */
export function Loading({ variant = 'lines' }: { variant?: 'lines' | 'cards' | 'rows' | 'screen' }) {
  return (
    <div role="status" aria-live="polite" className="flex flex-col gap-3" data-testid="loading">
      <span className="sr-only">{T.loading}</span>
      {variant === 'cards' ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex h-40 flex-col gap-3 rounded-md border border-border bg-card p-5">
              <div className="skeleton h-5 w-2/3" />
              <div className="skeleton h-4 w-full" />
              <div className="skeleton mt-auto h-4 w-1/3" />
            </div>
          ))}
        </div>
      ) : variant === 'rows' ? (
        <div className="flex flex-col divide-y divide-border rounded-md border border-border bg-card" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex min-h-14 items-center gap-4 px-4">
              <div className="skeleton h-4 w-1/3" />
              <div className="skeleton h-4 w-16" />
              <div className="skeleton ms-auto h-4 w-24" />
            </div>
          ))}
        </div>
      ) : variant === 'screen' ? (
        <div className="flex flex-col gap-3" aria-hidden="true">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="skeleton h-24" />
            ))}
          </div>
          <div className="skeleton h-72" />
        </div>
      ) : (
        <div className="flex flex-col gap-2" aria-hidden="true">
          <div className="skeleton h-7 w-1/3" />
          <div className="skeleton h-4 w-2/3" />
          <div className="skeleton h-4 w-1/2" />
        </div>
      )}
    </div>
  );
}

/** מצב ריק: אייקון, כותרת, הסבר ופעולה אופציונלית. */
export function EmptyState({ icon, title, children, className }: { icon?: ReactNode; title: string; children?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center gap-2 rounded-md border border-dashed border-border bg-card/50 px-6 py-10 text-center', className)}>
      {icon && <span className="mb-1 grid size-12 place-items-center rounded-circle bg-primary-soft text-primary">{icon}</span>}
      <p className="m-0 font-semibold">{title}</p>
      {children && <div className="text-sm text-muted-foreground">{children}</div>}
    </div>
  );
}
